import { IPermissionsResponse } from '@/domain/permissions/interfaces/permissions';
import { permissionsRepository } from '@/domain/permissions/repositories/permissionsRepository';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { createStore } from '@/lib/zustand';

/**
 * What the signed-in person may do in the active account, from `GET /permissions`. Session data:
 * the menu, the route gate and the write controls of every screen read it, so it loads once per
 * account (`usePermissionsBootstrap`) and is reset by the account switch like every store (the
 * role, and with it the list, belongs to the account).
 */
interface PermissionsState {
  /** `null` until the first answer, so nothing gated is drawn before the API said it may be. */
  answer: IPermissionsResponse | null;
  loading: boolean;
  failed: boolean;
  load: () => Promise<void>;
}

/** Only the latest request writes: an answer for the previous account arrives too late to count. */
let requestSeq = 0;

export const permissionsStore = createStore<PermissionsState>((set) => ({
  answer: null,
  loading: false,
  failed: false,

  load: async () => {
    requestSeq += 1;
    const seq = requestSeq;
    set({ loading: true, failed: false });
    try {
      const answer = await permissionsRepository.getPermissions();
      if (seq !== requestSeq) {
        return;
      }
      set({ answer });
    } catch (error) {
      // The screen fails open: the API still refuses what the role does not allow.
      logger.error('[permissionsStore] failed to load the permissions', error);
      if (seq === requestSeq) {
        set({ failed: true });
      }
    } finally {
      if (seq === requestSeq) {
        set({ loading: false });
      }
    }
  },
}));
